import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Skeleton, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { COLORS } from '../../../shared/colors';

// Helper component for shimmer animations with a premium, soft color
const WaveSkeleton = (props) => (
  <Skeleton 
    animation="wave" 
    sx={{ 
      bgcolor: 'rgba(235, 240, 250, 0.6)', 
      borderRadius: '4px',
      ...props.sx 
    }} 
    {...props} 
  />
);

// Individual Kanban Card Skeleton
export const TaskCardSkeleton = () => (
  <Card 
    sx={{ 
      borderRadius: '8px', 
      border: '0.5px solid rgba(8, 43, 114, 0.1)', 
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
      mb: 1.5,
      bgcolor: '#FFFFFF'
    }}
  >
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      {/* Top row: Code badge and icon placeholder */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <WaveSkeleton variant="rounded" width={60} height={16} />
        <WaveSkeleton variant="circular" width={16} height={16} />
      </Box>

      {/* Ticket Title: Two lines of shimmer */}
      <WaveSkeleton variant="text" width="90%" height={20} mb={0.5} />
      <WaveSkeleton variant="text" width="60%" height={20} mb={1.5} />

      {/* Divider */}
      <Box sx={{ borderTop: '0.5px solid rgba(8, 43, 114, 0.08)', my: 1 }} />

      {/* Bottom row: Assignee avatar & Priority/Date chips */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {/* Assignees Avatars */}
        <Box display="flex" gap={-0.5}>
          <WaveSkeleton variant="circular" width={22} height={22} />
        </Box>
        {/* Priority & Date Chips */}
        <Box display="flex" gap={1}>
          <WaveSkeleton variant="rounded" width={40} height={16} />
          <WaveSkeleton variant="rounded" width={55} height={16} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Individual Table Row Skeleton (used in List view)
export const ListRowSkeleton = () => (
  <TableRow sx={{ height: 48 }}>
    {/* Task Title */}
    <TableCell sx={{ width: '25%' }}>
      <Box display="flex" alignItems="center" gap={1.5}>
        <WaveSkeleton variant="circular" width={18} height={18} />
        <WaveSkeleton variant="text" width="80%" height={20} />
      </Box>
    </TableCell>
    {/* Code */}
    <TableCell sx={{ width: '10%' }}>
      <WaveSkeleton variant="rounded" width={50} height={20} />
    </TableCell>
    {/* Priority */}
    <TableCell sx={{ width: '12%' }}>
      <Box display="flex" alignItems="center" gap={1}>
        <WaveSkeleton variant="circular" width={12} height={12} />
        <WaveSkeleton variant="text" width={50} height={18} />
      </Box>
    </TableCell>
    {/* Assignee */}
    <TableCell sx={{ width: '15%' }}>
      <WaveSkeleton variant="circular" width={24} height={24} />
    </TableCell>
    {/* Labels */}
    <TableCell sx={{ width: '8%' }}>
      <WaveSkeleton variant="rounded" width={45} height={18} />
    </TableCell>
    {/* Estimate Time */}
    <TableCell sx={{ width: '10%' }}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <WaveSkeleton variant="circular" width={12} height={12} />
        <WaveSkeleton variant="text" width={25} height={18} />
      </Box>
    </TableCell>
    {/* Due Date */}
    <TableCell sx={{ width: '10%' }}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <WaveSkeleton variant="circular" width={12} height={12} />
        <WaveSkeleton variant="text" width={60} height={18} />
      </Box>
    </TableCell>
    {/* Actions */}
    <TableCell sx={{ width: '10%' }}>
      <WaveSkeleton variant="circular" width={20} height={20} sx={{ ml: 'auto' }} />
    </TableCell>
  </TableRow>
);

// Entire Board View Skeleton (Kanban columns)
export const BoardViewSkeleton = () => {
  // Simulate 5 columns (To Do, In Progress, Review, Blocked, Done)
  const columns = [
    { titleWidth: 50, color: 'rgba(59, 130, 246, 0.05)' },
    { titleWidth: 75, color: 'rgba(245, 158, 11, 0.05)' },
    { titleWidth: 60, color: 'rgba(139, 92, 246, 0.05)' },
    { titleWidth: 55, color: 'rgba(239, 68, 68, 0.05)' },
    { titleWidth: 45, color: 'rgba(16, 185, 129, 0.05)' }
  ];

  return (
    <Box 
      display="flex" 
      gap={2} 
      sx={{ 
        minWidth: 'max-content', 
        height: '100%', 
        px: 2, 
        py: 2,
        overflow: 'hidden'
      }}
    >
      {columns.map((col, index) => (
        <Box
          key={index}
          sx={{
            width: 290,
            backgroundColor: col.color,
            borderRadius: '12px',
            padding: '8px',
            border: '0.5px solid rgba(8, 43, 114, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          {/* Column Header Skeleton */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={1.5}
            py={1.5}
            sx={{ 
              bgcolor: 'white',
              border: '0.5px solid rgba(8, 43, 114, 0.1)',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
            }}
          >
            <Box display="flex" alignItems="center" gap={1} width="80%">
              <WaveSkeleton variant="circular" width={16} height={16} />
              <WaveSkeleton variant="text" width={col.titleWidth} height={20} />
              <WaveSkeleton variant="rounded" width={20} height={16} sx={{ borderRadius: '10px' }} />
            </Box>
            <WaveSkeleton variant="circular" width={16} height={16} />
          </Box>

          {/* Add Task Button Skeleton */}
          <Box 
            sx={{ 
              mb: 1, 
              mt: 0.5, 
              height: 36, 
              borderRadius: 2, 
              bgcolor: 'rgba(8, 43, 114, 0.04)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <WaveSkeleton variant="text" width={70} height={18} />
          </Box>

          {/* Column Tasks Scrollable Container */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Render 3 Task Card Skeletons per column */}
            <TaskCardSkeleton />
            <TaskCardSkeleton />
            <TaskCardSkeleton />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// Entire List View Skeleton (Accordions + Tables)
export const ListViewSkeleton = () => {
  // Simulate 3 accordion status sections
  const statuses = [
    { title: 'To Do', width: 60, rows: 3 },
    { title: 'In Progress', width: 80, rows: 4 },
    { title: 'Done', width: 50, rows: 2 }
  ];

  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: '100%', 
        overflowY: 'auto', 
        pb: 8,
        px: 2,
        pt: 2
      }}
    >
      {statuses.map((status, index) => (
        <Accordion 
          key={index}
          expanded={false} 
          sx={{ 
            mb: 1.5, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)', 
            border: `1px solid ${COLORS.gray[200]}`,
            borderRadius: '8px !important',
            overflow: 'hidden',
            pointerEvents: 'none' // keep interactive visual only
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon sx={{ color: COLORS.gray[400] }} />}
            sx={{ 
              bgcolor: COLORS.gray[50], 
              height: 48,
              '& .MuiAccordionSummary-content': { margin: '12px 0' }
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5} width="100%">
              <DragIndicatorIcon sx={{ fontSize: 16, color: COLORS.gray[300] }} />
              <WaveSkeleton variant="circular" width={16} height={16} />
              <WaveSkeleton variant="text" width={status.width} height={20} />
              <WaveSkeleton variant="rounded" width={32} height={18} sx={{ borderRadius: '10px' }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: COLORS.gray[50], borderBottom: '1px solid rgba(8, 43, 114, 0.08)' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '13px', width: '25%' }}>Task</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '13px', width: '10%' }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '13px', width: '12%' }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '13px', width: '15%' }}>Assignee</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '13px', width: '8%' }}>Labels</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '13px', width: '10%' }}>Est Time</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '13px', width: '10%' }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '13px', width: '10%' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: status.rows }).map((_, rowIndex) => (
                    <ListRowSkeleton key={rowIndex} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};
